/**
 * Order totals calculation matching backend (Order::getOrderTotalsStruct).
 * Percentage discounts: applied per line before tax. Dollar discounts: applied post-tax to order total only.
 */

export const TAX_RATE = 0.13;

export function round2 (value) {
    return Math.round(Number(value) * 100) / 100;
}

/**
 * Get delivery fee from order (matches backend deliveryFee(): fee_for_delivery, delivery_fee, or shipping_fee).
 */
export function getOrderDeliveryFee (order) {
    if (order.fee_for_delivery != null && order.fee_for_delivery !== undefined) {
        return parseFloat(order.fee_for_delivery) || 0;
    }
    if (order.delivery_fee != null && order.delivery_fee !== undefined) {
        return parseFloat(order.delivery_fee) || 0;
    }
    if (order.shipping_fee != null && order.shipping_fee !== undefined) {
        return parseFloat(order.shipping_fee) || 0;
    }
    return 0;
}

/**
 * Whether percentage promo discounts should skip items marked prc_promo_code_exempt.
 * Unlimited 100% codes ignore the exemption; all other percentage promos respect it.
 * Mirrors Order::percentagePromoUsesProductExemption() / PromoCode::percentageDiscountIgnoresProductExemption().
 *
 * @param {Object} order
 * @returns {boolean}
 */
export function percentagePromoUsesProductExemption (order) {
    const pct = order.promo_code_percent;
    if (pct == null || pct === '') {
        return false;
    }

    const promo = order.applied_promo_code;
    if (!promo) {
        return true;
    }

    if (promo.type !== 'prc_discount') {
        return false;
    }

    const amount = Number(promo.discount_amount);
    const usage = Number(promo.usage);
    if (amount >= 100 && usage === 0) {
        return false;
    }

    return true;
}

/**
 * Compute order totals from an order object. Mirrors backend Order::getOrderTotalsStruct() and total().
 * @param {Object} order - Order with order_items, customer, promo_code_percent, promo_code_amount, applied_promo_code, applied_credit_amount
 * @returns {{ subtotal, subtotalBeforeDiscount, tax, deliveryFee, deliveryFeeAfterDiscount, promoTotal, totalBeforeCredit, total, lineDiscountedTotals }}
 */
export function computeOrderTotals (order) {
    const items = order.order_items || [];
    const taxExempt = order.tax_exempt === 1
        || (order.tax_exempt == null && !!(order.customer && order.customer.tax_exempt));
    const hasPromo = !!(order.promo_code_id || order.applied_promo_code);
    const hasPercentDiscount = hasPromo && order.promo_code_percent != null;
    const hasDollarDiscount = hasPromo && Number(order.promo_code_amount) > 0 && order.promo_code_percent == null;
    const respectsExemption = hasPercentDiscount && percentagePromoUsesProductExemption(order);

    let subtotalBeforeDiscount = 0;
    const lineAmountsBefore = [];
    const lineAmountsAfter = [];

    items.forEach((item) => {
        const lineBefore = (Number(item.price) || 0) * (Number(item.quantity) || 0);
        subtotalBeforeDiscount += lineBefore;

        const eligible = !(respectsExemption && item.prc_promo_code_exempt);

        let lineAfter;
        if (!hasPercentDiscount) {
            lineAfter = round2(lineBefore);
        } else if (eligible) {
            // Match BC sales-line rounding: rounded gross minus a separately rounded discount
            const gross = round2(lineBefore);
            const pct = Number(order.promo_code_percent);
            lineAfter = round2(gross - round2(gross * pct / 100));
        } else {
            lineAfter = round2(lineBefore);
        }

        lineAmountsBefore.push(lineBefore);
        lineAmountsAfter.push({ after: lineAfter, eligible });
    });

    const deliveryFee = getOrderDeliveryFee(order);
    let deliveryFeeAfterDiscount = deliveryFee;
    let subtotalAfterDiscount = 0;
    let promoTotal = 0;
    let dollarDiscountPostTax = false;

    if (hasDollarDiscount) {
        subtotalAfterDiscount = round2(subtotalBeforeDiscount);
        deliveryFeeAfterDiscount = deliveryFee;
        promoTotal = Number(order.promo_code_amount) || 0;
        dollarDiscountPostTax = true;
    } else if (hasPercentDiscount) {
        lineAmountsAfter.forEach((la) => { subtotalAfterDiscount += la.after; });
        subtotalAfterDiscount = round2(subtotalAfterDiscount);
        const deliveryGross = round2(deliveryFee);
        const deliveryPct = Number(order.promo_code_percent);
        deliveryFeeAfterDiscount = round2(deliveryGross - round2(deliveryGross * deliveryPct / 100));
        promoTotal = round2(subtotalBeforeDiscount + deliveryFee - (subtotalAfterDiscount + deliveryFeeAfterDiscount));
    } else {
        lineAmountsAfter.forEach((la) => { subtotalAfterDiscount += la.after; });
        subtotalAfterDiscount = round2(subtotalAfterDiscount);
    }

    // For dollar discount: full line amounts (no proportional allocation). For percentage: discounted line.
    const lineDiscountedTotals = {};
    if (hasDollarDiscount) {
        items.forEach((item, i) => {
            lineDiscountedTotals[item.id] = round2(lineAmountsBefore[i]);
        });
    } else {
        items.forEach((item, i) => {
            lineDiscountedTotals[item.id] = lineAmountsAfter[i].after;
        });
    }

    let itemTax = 0;
    let deliveryFeeTax = 0;
    if (!taxExempt) {
        items.forEach((item, i) => {
            if (item.tax) {
                itemTax += round2(lineAmountsAfter[i].after * TAX_RATE);
            }
        });
        deliveryFeeTax = round2(deliveryFeeAfterDiscount * TAX_RATE);
    }

    const tax = round2(itemTax + deliveryFeeTax);
    let totalBeforeCredit = round2(subtotalAfterDiscount + deliveryFeeAfterDiscount + itemTax + deliveryFeeTax);
    // Cap dollar/gift-card promo at order total so remainder stays on the code.
    if (dollarDiscountPostTax) {
        promoTotal = round2(Math.min(promoTotal, totalBeforeCredit));
        totalBeforeCredit = round2(totalBeforeCredit - promoTotal);
    }
    const appliedCredit = Number(order.applied_credit_amount) || 0;
    const total = round2(totalBeforeCredit - appliedCredit);

    return {
        subtotalBeforeDiscount: round2(subtotalBeforeDiscount),
        subtotal: subtotalAfterDiscount,
        itemTax: round2(itemTax),
        deliveryFeeTax,
        tax,
        deliveryFee,
        deliveryFeeAfterDiscount,
        promoTotal: round2(promoTotal),
        totalBeforeCredit,
        total,
        lineDiscountedTotals
    };
}

/**
 * Get line discounted total for one order item (for partial refund display). Use order's lineDiscountedTotals from computeOrderTotals, or pass order for a single lookup.
 */
export function getLineDiscountedTotal (order, orderItem, refundQuantity = null) {
    const totals = computeOrderTotals(order);
    const lineTotal = totals.lineDiscountedTotals[orderItem.id] ?? round2((Number(orderItem.price) || 0) * (Number(orderItem.quantity) || 0));
    if (refundQuantity != null && orderItem.quantity > 0) {
        return round2((lineTotal / Number(orderItem.quantity)) * Number(refundQuantity));
    }
    return lineTotal;
}

/**
 * Per-line discount amount (before discount minus line discounted total). Zero for dollar discounts; positive for percentage discounts.
 */
export function lineDiscountAmount (order, orderItem) {
    const lineBefore = (Number(orderItem.price) || 0) * (Number(orderItem.quantity) || 0);
    return round2(lineBefore - getLineDiscountedTotal(order, orderItem));
}

export default function useOrderTotals () {
    return {
        round2,
        getOrderDeliveryFee,
        percentagePromoUsesProductExemption,
        computeOrderTotals,
        getLineDiscountedTotal,
        lineDiscountAmount,
        TAX_RATE
    };
}
